import {TechTreeComponent} from "../../models/remoteConstants.model";
import {CELL, cellKey} from "../../utils/graphCanvas";
import {ROOT_ID} from "../../utils/techTree.utils";

// Derives what the Tech Tree canvas draws from the raw techTree array: every
// node with a grid position, a parent index, and the edge list.
//
// Root is deliberately absent from `nodes`, `edges` and `occupied`: it's an
// anchor, not a tech, so the canvas doesn't draw it and nothing should be able
// to click it, collide with its cell, or draw a line to it. It still seeds the
// fallback layout below (the radial arrangement grows from it) and still appears
// in `parents`, so a tier-1 node counts as reachable rather than orphaned.
//
// Positions are authored (tech.x / tech.y) when present. Nodes that have never
// been placed fall back to the radial layout the tab used before it became a
// grid editor, quantised onto cells — so opening an untouched tree shows the
// arrangement designers already know rather than rearranging it under them.
// Nothing is written to the model until someone drags a node or presses Place.
//
// One deliberate difference from the old view: it iterates ALL of
// state.techTree. The old hierarchy walk only flattened what it could reach
// from the literal id 'root', so an orphaned node (or anything under a node
// with 3+ parents) was invisible in the editor while still being live data.
// Orphans now render in their own column.

export interface TechGraphNode {
    tech: TechTreeComponent;
    x: number;
    y: number;
    // false = this position came from the fallback layout, not from the model.
    placed: boolean;
    depth: number;
    orphan: boolean;
}

export interface TechGraphEdge {
    parent: string;
    child: string;
}

export interface TechGraph {
    // Drawable nodes — root excluded, see the note at the top of this file.
    nodes: TechGraphNode[];
    byId: Map<string, TechGraphNode>;
    // Every dependency, root's included, so orphan detection is right.
    parents: Map<string, string[]>;
    // Drawable edges — those out of root excluded, since it has no circle for
    // them to start from.
    edges: TechGraphEdge[];
    // Cell key -> node id, for collision checks while dragging.
    occupied: Map<string, string>;
}

// Legacy world px per grid cell when converting the original radial layout into
// cells. The original spacing (200px to the first ring, +280 per ring after) was
// tuned for ~200px-wide node boxes, so it compresses onto a grid of 88px circles.
//
// This is deliberately CELL-relative rather than absolute: it works out to a bit
// under 4 cells between rings, which at CELL = 60 keeps the tree's real extent
// around 1800px — tight enough that Fit lands at a zoom where names are still
// legible, and loose enough that rings never collide after rounding. Because it
// scales with CELL, changing the grid resolution re-quantises the same layout
// rather than resizing it. Raising the multiplier compresses the tree.
const FALLBACK_PX_PER_CELL = CELL * (7 / 6);

export function buildTechGraph(techTree: TechTreeComponent[] | undefined): TechGraph {
    const techs = techTree ?? [];

    // Parent index, built once. The old view rescanned every node for every
    // node on every render (O(n²)).
    const parents = new Map<string, string[]>();
    const edges: TechGraphEdge[] = [];
    for (const tech of techs) {
        for (const child of tech.children ?? []) {
            // Root's edges are indexed as parents but never drawn.
            if (tech.id !== ROOT_ID) edges.push({parent: tech.id, child});
            const list = parents.get(child);
            if (list) list.push(tech.id);
            else parents.set(child, [tech.id]);
        }
    }

    const fallback = fallbackLayout(techs, parents);

    const nodes: TechGraphNode[] = [];
    const byId = new Map<string, TechGraphNode>();
    const occupied = new Map<string, string>();

    for (const tech of techs) {
        // Not drawn, so it must not be in the node list, take a cell, or be
        // hit-testable. Its fallback placement is still computed above, because
        // the rest of the radial layout is measured from it.
        if (tech.id === ROOT_ID) continue;

        const placed = Number.isInteger(tech.x) && Number.isInteger(tech.y);
        const computed = fallback.get(tech.id);
        const cell = placed ? {x: tech.x as number, y: tech.y as number} : (computed?.cell ?? {x: 0, y: 0});
        const node: TechGraphNode = {
            tech,
            x: cell.x,
            y: cell.y,
            placed,
            depth: computed?.depth ?? 0,
            orphan: !parents.has(tech.id),
        };
        nodes.push(node);
        byId.set(tech.id, node);
        // First writer wins, so a duplicate authored cell leaves the later node
        // reported as the colliding one rather than silently hiding the first.
        if (!occupied.has(cellKey(cell.x, cell.y))) occupied.set(cellKey(cell.x, cell.y), tech.id);
    }

    return {nodes, byId, parents, edges, occupied};
}

interface FallbackPlacement {
    cell: {x: number; y: number};
    depth: number;
}

// The pre-grid radial layout, quantised onto cells.
//
// The trig below is carried over verbatim from the original circularTree so the
// shape is the one designers are used to: root at the centre, its children
// spread evenly all the way round, and each subtree marching outward along its
// own ray one ring per level.
function fallbackLayout(
    techs: TechTreeComponent[],
    parents: Map<string, string[]>,
): Map<string, FallbackPlacement> {
    const byId = new Map(techs.map(t => [t.id, t]));
    const childrenOf = fallbackTree(techs);

    // Lay out from 'root' when it exists (it always does in practice), else from
    // the first parentless node so a tree missing its root still draws.
    const start = byId.has(ROOT_ID)
        ? ROOT_ID
        : techs.find(t => !parents.has(t.id))?.id;

    // Pre-order, so a parent always claims its preferred cell before its
    // children have to dodge around it.
    const ordered: {id: string; px: number; py: number; depth: number}[] = [];
    const visited = new Set<string>();

    function walk(id: string, depth: number, angle: number, t: number) {
        // Guards against a cycle in the data, which would otherwise recurse
        // until the stack blows.
        if (visited.has(id)) return;
        visited.add(id);

        const radius = 200 + 280 * (depth - 1);
        ordered.push({id, px: radius * Math.cos(angle), py: radius * Math.sin(angle), depth});

        const children = childrenOf.get(id) ?? [];
        children.forEach((child, i) => {
            const childAngle = angle
                + fanAngle(i, children.length, t)
                - fanAngle(children.length - 1, children.length, t) / 2;
            walk(child, depth + 1, childAngle, 4 + depth * 4);
        });
    }

    if (start !== undefined) walk(start, 0, 0, 1);

    const result = new Map<string, FallbackPlacement>();
    const taken = new Set<string>();
    for (const entry of ordered) {
        // Root goes dead centre. The original put it at (-80, 0) while drawing
        // every edge to it as if it were at the origin — a long-standing
        // misalignment there's no reason to carry over.
        const ideal = entry.id === start
            ? {x: 0, y: 0}
            : {
                x: Math.round(entry.px / FALLBACK_PX_PER_CELL),
                y: Math.round(entry.py / FALLBACK_PX_PER_CELL),
            };
        const cell = nearestFreeCell(ideal, taken);
        taken.add(cellKey(cell.x, cell.y));
        result.set(entry.id, {cell, depth: entry.depth});
    }

    // Anything the walk never reached — orphans, and descendants of nodes with
    // three or more parents — goes in a column clear of the tree, so it's
    // visible and obviously detached rather than silently missing.
    const unreached = techs.filter(t => !result.has(t.id));
    if (unreached.length > 0) {
        let columnX = 3;
        result.forEach(p => {
            columnX = Math.max(columnX, p.cell.x + 3);
        });
        unreached.forEach((tech, i) => {
            const cell = nearestFreeCell({x: columnX, y: i - Math.floor(unreached.length / 2)}, taken);
            taken.add(cellKey(cell.x, cell.y));
            result.set(tech.id, {cell, depth: 0});
        });
    }

    return result;
}

// Attaches each node to the first node in array order that lists it as a child,
// pushing in reverse array order. Both details are inherited from the original
// layout: they decide which compass direction each subtree ends up in, so
// changing them would silently rotate every existing tree.
function fallbackTree(techs: TechTreeComponent[]): Map<string, string[]> {
    const childrenOf = new Map<string, string[]>();
    for (let i = techs.length - 1; i >= 0; i--) {
        const id = techs[i].id;
        const parent = techs.find(t => (t.children ?? []).includes(id));
        if (parent === undefined) continue;
        const list = childrenOf.get(parent.id);
        if (list) list.push(id);
        else childrenOf.set(parent.id, [id]);
    }
    return childrenOf;
}

function fanAngle(i: number, len: number, t: number): number {
    return (i * (Math.PI / t)) / (len / 2);
}

// The free cell closest to `ideal`, searched outward ring by ring. Keeps a
// quantised node near the direction its trig position pointed, instead of
// sliding it down a column.
function nearestFreeCell(ideal: {x: number; y: number}, taken: Set<string>): {x: number; y: number} {
    if (!taken.has(cellKey(ideal.x, ideal.y))) return ideal;

    for (let r = 1; r <= 12; r++) {
        let best: {x: number; y: number} | undefined;
        let bestDistSq = Infinity;
        for (let dx = -r; dx <= r; dx++) {
            for (let dy = -r; dy <= r; dy++) {
                // Perimeter of this ring only; the interior was covered already.
                if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
                if (taken.has(cellKey(ideal.x + dx, ideal.y + dy))) continue;
                const distSq = dx * dx + dy * dy;
                if (distSq < bestDistSq) {
                    bestDistSq = distSq;
                    best = {x: ideal.x + dx, y: ideal.y + dy};
                }
            }
        }
        if (best) return best;
    }

    // Densely packed beyond all reason — overlapping beats vanishing.
    return ideal;
}
